import { Component, HostBinding, Input, OnChanges, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Form, Frequency } from '@vendasta/galaxy/frequency';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { Commitment, Discount, Fee, FeeAmountType, PricingRule } from '@vendasta/marketplace-apps';
import { labelForGalaxyFrequency } from '../utils/frequency';
import { SimplePrice } from './simple-price';

export const baseTranslationKey = 'PRICING_COMMONS.';
export const startingAtTranslationKey = 'STARTING_AT_LABEL';
export const minimumTranslationKey = 'MINIMUM_PRICE_LABEL';
export const freeTranslationKey = 'FREE_LABEL';
export const contactSalesTranslationKey = 'CONTACT_SALES_LABEL';
export const setupFeeTranslationKey = 'SETUP_FEE_LABEL';
export const managementFeeTranslationKey = 'MANAGEMENT_FEE_LABEL';
export const priceUnavailableTranslationKey = 'PRICE_UNAVAILABLE_LABEL';

let STARTING_AT_LABEL: string;
let MINIMUM_LABEL: string;
let FREE_LABEL: string;
let CONTACT_SALES_LABEL: string;
let PRICE_UNAVAILABLE_LABEL: string;

@Component({
  selector: 'billing-ui-simple-price-display',
  templateUrl: './simple-price-display.component.html',
  styleUrls: ['./simple-price-display.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SimplePriceDisplayComponent implements OnChanges, OnInit {
  FeeAmountType = FeeAmountType; //Fee package
  Frequency = Frequency; //Frequency package
  @Input() price: number;
  @Input() showGreenPrice: boolean;
  @Input() prices: SimplePrice[];
  @Input() currencyCode = 'USD';
  @Input() frequency: Frequency;
  @Input() contactSales = false;
  @Input() topLabel: 'starting-at' | 'minimum' | null;

  @Input() stairSteps: PricingRule[] = [];
  @Input() commitment: Commitment;
  @Input() discounts: Discount[];
  @Input() fees: Fee[];
  @Input() setupFee: number;

  @Input() componentTextLength: 'short' | 'verbose' | 'long' = 'short';
  @Input() view: 'list-view' | 'card-view' = 'list-view';
  size: 'regular' | 'large' = 'regular';
  @Input() align: 'start' | 'end' | 'center' = 'start';
  @Input() priceUnavailable = false;
  @Input() priceUnavailableStyle: 'default' | 'dash' = 'default';
  @Input() allowNegativePrice = false;
  @Input() allowCustomStyling = false;
  @Input() alwaysShowNumber = false;
  @Input() fixedTopHeight = false; // Keeps the top label area the same height horizontally across multiple components
  @Input() useUnderlineOnlyForPopover = false;

  @HostBinding('class.use-underline-only-for-popover') get applyUnderlineOnlyClass(): boolean {
    return this.useUnderlineOnlyForPopover;
  }

  get frequencyForm(): Form {
    return this.componentTextLength as Form;
  }

  //labels
  topLabelDisplay: string;
  overriddenValue: string;
  popoverTitle: string;
  initialCommitment: string;
  recurringCommitment: string;
  setupFeeLabelKey = baseTranslationKey + setupFeeTranslationKey;
  managementFeeLabelKey = baseTranslationKey + managementFeeTranslationKey;
  popoverPositions = PopoverPositions;

  //price info
  managementFeeFrequency: string | undefined;
  multiFrequency = false;

  //display booleans
  shouldShowPopover: boolean;
  shouldShowBottomInfo: boolean;
  hasStairStepPricing = false;
  hasDiscounts: boolean;
  isCardView: boolean;
  showingPopover = false;

  constructor(private translateService: TranslateService) {}
  ngOnInit(): void {
    this.loadTranslations();
  }

  ngOnChanges(): void {
    // Make sure the translations were properly loaded on init
    if (STARTING_AT_LABEL === undefined) {
      this.loadTranslations();
    }

    if (this.prices) {
      this.prices.sort((a, b) => {
        switch (a.frequency) {
          case Frequency.MONTHLY:
            switch (b.frequency) {
              case Frequency.MONTHLY:
                return 0;
              case Frequency.YEARLY:
                return -1;
              case Frequency.ONE_TIME:
                return -1;
            }
            break;
          case Frequency.YEARLY:
            switch (b.frequency) {
              case Frequency.MONTHLY:
                return 1;
              case Frequency.YEARLY:
                return 0;
              case Frequency.ONE_TIME:
                return -1;
            }
            break;
          case Frequency.ONE_TIME:
            switch (b.frequency) {
              case Frequency.MONTHLY:
                return 1;
              case Frequency.YEARLY:
                return 1;
              case Frequency.ONE_TIME:
                return 0;
            }
            break;
        }

        return 0;
      });
      if (!this.contactSales) {
        this.multiFrequency = true;
      }
    }

    if (this.price === undefined) {
      this.overriddenValue = PRICE_UNAVAILABLE_LABEL;
    }

    this.isCardView = this.view.toLowerCase() === 'card-view';
    if (this.isCardView) {
      this.size = 'large';
    } else {
      this.size = 'regular';
    }

    const shouldDisplayProductFreePrice = !this.price && !this.alwaysShowNumber && this.prices == null;
    const shouldDisplayPackageFreePrice =
      this.prices && this.prices.length == 1 && !this.prices[0].price && !this.alwaysShowNumber;
    if (this.contactSales) {
      this.setContactSalesOverride();
      return;
    } else if (this.priceUnavailable) {
      this.overriddenValue = PRICE_UNAVAILABLE_LABEL;
    } else if (shouldDisplayProductFreePrice || shouldDisplayPackageFreePrice) {
      this.overriddenValue = FREE_LABEL;
    } else {
      this.overriddenValue = '';
    }

    // Checks if it's a stairstep price
    if (this.stairSteps?.length > 1) {
      this.hasStairStepPricing = true;
    } else {
      this.hasStairStepPricing = false;
    }

    if (this.topLabel) {
      this.setTopLabel();
    }

    this.setCommitmentLabel();

    this.managementFeeFrequency =
      this.componentTextLength === 'short'
        ? labelForGalaxyFrequency(this.frequency, true, true)
        : labelForGalaxyFrequency(this.frequency, true);

    this.hasDiscounts = this.discounts !== undefined && this.discounts.length > 0;

    let hasCommitments = false;
    if (this.commitment?.initial > 1 || this.commitment?.recurring > 1) {
      hasCommitments = true;
    }

    this.shouldShowPopover =
      (hasCommitments || this.hasDiscounts || this.hasStairStepPricing) &&
      !this.isCardView &&
      this.overriddenValue !== CONTACT_SALES_LABEL;
    this.shouldShowBottomInfo = (hasCommitments || this.hasDiscounts) && this.isCardView;

    if (this.shouldShowPopover) {
      this.popoverTitle = 'Wholesale pricing information';
    }
  }

  loadTranslations(): void {
    STARTING_AT_LABEL = this.translateService.instant(`${baseTranslationKey}${startingAtTranslationKey}`);
    MINIMUM_LABEL = this.translateService.instant(`${baseTranslationKey}${minimumTranslationKey}`);
    FREE_LABEL = this.translateService.instant(`${baseTranslationKey}${freeTranslationKey}`);
    CONTACT_SALES_LABEL = this.translateService.instant(`${baseTranslationKey}${contactSalesTranslationKey}`);
    if (this.priceUnavailableStyle == 'dash') {
      PRICE_UNAVAILABLE_LABEL = '-';
    } else {
      PRICE_UNAVAILABLE_LABEL = this.translateService.instant(`${baseTranslationKey}${priceUnavailableTranslationKey}`);
    }
  }

  setTopLabel(): void {
    if (this.topLabel === 'starting-at') {
      this.topLabelDisplay = STARTING_AT_LABEL;
    } else if (this.topLabel === 'minimum') {
      this.topLabelDisplay = MINIMUM_LABEL;
    }
  }

  setContactSalesOverride(): void {
    this.overriddenValue = CONTACT_SALES_LABEL;
  }

  setCommitmentLabel(): void {
    if (this.commitment) {
      this.commitment.initial = this.commitment.initial || 1;
      this.commitment.recurring = this.commitment.recurring || 1;

      if (this.frequency === Frequency.ONE_TIME || (this.commitment?.initial <= 1 && this.commitment?.recurring <= 1)) {
        this.initialCommitment = '';
        this.recurringCommitment = '';
        return;
      }

      this.initialCommitment =
        this.commitment.initial + '-' + labelForGalaxyFrequency(this.frequency) + ' subscription';

      if (this.commitment?.recurring === 1) {
        this.recurringCommitment =
          'Renews every ' + this.commitment.recurring + ' ' + labelForGalaxyFrequency(this.frequency) + ' afterward';
      } else {
        this.recurringCommitment =
          'Renews every ' + this.commitment.recurring + ' ' + labelForGalaxyFrequency(this.frequency) + 's afterward';
      }
    } else {
      this.initialCommitment = '';
      this.recurringCommitment = '';
      return;
    }
  }

  displayPopover(): void {
    if (this.shouldShowPopover) {
      this.showingPopover = true;
    }
  }
}
